import React from 'react';
import { Box, Spinner, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
  fullScreen?: boolean;
}

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = 'md', text = 'Loading...', fullScreen = false }) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const bgColor = useColorModeValue('white', 'gray.800');

  const spinnerSize = {
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl',
  }[size];

  const content = (
    <VStack spacing={4} align="center">
      <Spinner
        size={spinnerSize}
        color="brand.500"
        thickness="3px"
        speed="0.8s"
        emptyColor="gray.200"
        _dark={{ emptyColor: 'gray.600' }}
      />
      {text && (
        <Text
          color={textColor}
          fontSize={{ base: 'sm', md: 'md' }}
          fontWeight="medium"
          animation={`${pulse} 2s ease-in-out infinite`}
        >
          {text}
        </Text>
      )}
    </VStack>
  );

  if (fullScreen) {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg={bgColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={9999}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" justifyContent="center" py={{ base: 8, md: 12 }} px={4}>
      {content}
    </Box>
  );
};

export default LoadingSpinner;
